import React, { FC, MouseEvent, ReactNode, useCallback } from 'react';
import { Text } from '@cubeyou-team/hx-react-components';

const SECTIONS = [
  { id: 'background', title: '1. Background' },
  { id: 'license', title: '2. License' },
  { id: 'term', title: '3. Term' },
  { id: 'telmarhelixa-rights', title: '4. TelmarHelixa’s Rights' },
  { id: 'feedback', title: '5. Feedback' },
  { id: 'restrictions', title: '6. Restrictions' },
  { id: 'exclusion-of-warranties', title: '7. Exclusion of Warranties' },
  { id: 'limitation-of-liability', title: '8. Limitation of Liability' },
  { id: 'indemnification', title: '9. Indemnification' },
  { id: 'confidentiality', title: '10. Confidentiality' },
  { id: 'data-protection', title: '11. Data Protection' },
  { id: 'general', title: '12. General' },
  { id: 'annex-1', title: 'Annex 1' },
];

const ANNEX_HEADERS = [
  'If Licensee is domiciled in:',
  'The TelmarHelixa entity entering into these Terms is:',
  'Notices should be addressed to:',
  'Governing Law is:',
  'Courts with exclusive jurisdiction are:',
];

const LEGAL_EMAIL = 'legal@telmarhelixa.com';

const NoticesCell = () => (
  <Text tag="span" size="xbody">
    75 Varick Street, 3rd Floor, New York, NY 10013. Copy on email to{' '}
    <DocLink href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</DocLink>
  </Text>
);

const ANNEX_ROWS = [
  [
    'United States of America & Mexico, or any country in the Americas and Canada',
    'Telmar Information Services Corporation, a New York corporation; Telmar HMS Limited (for Canada only) OR Helixa Inc., a Delaware corporation',
    <NoticesCell />,
    'New York and controlling United States federal law.',
    'State of New York',
  ],
  [
    'France',
    'Telmar Peaktime SAS, with capital of EUR 667,00), registered in the Trade and Corporate Register of Nanterre at Number 477.765.820, with headquarters at 15 place de la République 75003 Paris. VAT Registration: FR58 477 765 820',
    <NoticesCell />,
    'France',
    'Paris, France',
  ],
  [
    'A country in Asia or the Pacific region (excluding India and mainland China)',
    'Telmar Asia Limited',
    <NoticesCell />,
    'England & Wales',
    'London, England',
  ],
  [
    'Mainland China',
    'Telmar Software (Shanghai) Limited',
    <NoticesCell />,
    'People’s Republic of China',
    'People’s Republic of China',
  ],
  [
    'A country in Europe, the Middle East, India or Africa, other than France.',
    'Telmar Communications Limited (no. 01431987); Telmar Media Systems (Pty) Limited (no. 93/05522/07) (for Africa only)',
    <NoticesCell />,
    'England & Wales',
    'London, England',
  ],
];

// `border-solid` is explicit because the app disables Tailwind's preflight,
// which normally sets a default border style.
const CELL_CLASS = 'border border-solid border-hx-light-darker align-top p-3';

const Section: FC<{ id: string; title: string; children: ReactNode }> = ({
  id,
  title,
  children,
}) => (
  <section id={id} className="mt-10">
    <Text tag="h2" size="xheadline" weight="bold">
      {title}
    </Text>
    <div className="mt-3 flex flex-col gap-3">{children}</div>
  </section>
);

const SubHeading: FC<{ children: ReactNode }> = ({ children }) => (
  <Text tag="h3" size="body" weight="bold" className="mt-2">
    {children}
  </Text>
);

const Paragraph: FC<{ children: ReactNode }> = ({ children }) => (
  <Text tag="p" size="xbody">
    {children}
  </Text>
);

const Bullets: FC<{ children: ReactNode }> = ({ children }) => (
  <ul className="flex list-disc flex-col gap-3 pl-6">{children}</ul>
);

const Bullet: FC<{ children: ReactNode }> = ({ children }) => (
  <li>
    <Text tag="span" size="xbody">
      {children}
    </Text>
  </li>
);

const DocLink: FC<{
  href: string;
  isExternal?: boolean;
  children: ReactNode;
}> = ({ href, isExternal, children }) => {
  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (isExternal || !href.startsWith('#')) return;

      const section = document.getElementById(href.slice(1));
      if (!section) return;

      event.preventDefault();
      section.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
      });
      window.history.replaceState(null, '', href);
    },
    [href, isExternal]
  );

  return (
    <Text
      tag="a"
      size="xbody"
      variant="primary"
      href={href}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
    >
      {children}
    </Text>
  );
};

export const TermsAndConditions = () => (
  // <NotLoggedLayout>
  <div className="flex w-full flex-col items-center">
    <div className="flex h-24 w-full items-center justify-center">
      <img
        alt="logo"
        width="305"
        height="50"
        src={`${process.env.PUBLIC_URL}/logo-default.svg`}
      />
    </div>

    <article className="w-full max-w-4xl px-6 py-10 md:px-12">
      <Text tag="h1" size="headline" weight="bold">
        DISCOVER TRIAL TERMS OF SERVICE
      </Text>

      <nav aria-label="Contents" className="mt-6">
        <ul className="flex list-disc flex-col pl-6">
          {SECTIONS.map(({ id, title }) => (
            <li key={id}>
              <DocLink href={`#${id}`}>{title}</DocLink>
            </li>
          ))}
        </ul>
      </nav>

      <Section id="background" title="1. Background">
        <Paragraph>
          TelmarHelixa (determined in accordance with Annex 1 of these terms of
          service (“<strong>Terms</strong>”)) owns and operates a platform which
          it makes available to users under a Software as a Service (SaaS)
          model. These Terms set out the terms upon which TelmarHelixa will
          provide such services. These Terms constitute a legally binding
          agreement between TelmarHelixa and the company on whose behalf you are
          accepting these Terms (“<strong>Licensee</strong>”). By clicking the
          box confirming your acceptance of these Terms, or otherwise indicating
          your acceptance of them through the Services, you agree to be bound by
          these Terms on behalf of Licensee and confirm that you have the
          requisite authority to bind Licensee to these Terms. TelmarHelixa and
          Licensee are each a “<strong>Party</strong>” and together the “
          <strong>Parties</strong>”. You, as an individual user of the Services,
          are referred to as “<strong>User</strong>”.
        </Paragraph>
      </Section>

      <Section id="license" title="2. License">
        <Paragraph>
          Licensee is hereby granted a limited, nonexclusive, non-transferable
          license for User to: (1) use a preview version of the TelmarHelixa
          Discover SaaS product (the “<strong>Services</strong>”) by accessing
          TelmarHelixa’s computer infrastructure (the “<strong>Platform</strong>
          ”); and (2) use the Services solely for the trial and evaluation of
          the Services (the “<strong>Purpose</strong>”). Any and all rights
          granted to Licensee under these Terms may only be used for the
          Purpose.
        </Paragraph>
      </Section>

      <Section id="term" title="3. Term">
        <Paragraph>
          These Terms commences on the date that User clicks to accept its terms
          (the “<strong>Effective Date</strong>”) and continues for a period of
          thirty (30) days (or once User has conducted five (5) searches through
          the Services, if earlier) at the end of which it shall automatically
          terminate (the “<strong>Evaluation Period</strong>”) unless terminated
          earlier as set forth herein. These Terms will terminate automatically
          if Licensee and/or User fails to comply with any of the limitations or
          other requirements described herein. At the end of the Evaluation
          Period or upon the request of TelmarHelixa, whichever is earlier,
          Licensee and User will cease to access the Services and/or Platform,
          and make no further use whatsoever of the Services and/or Platform
          except to the extent that may be permitted under any subsequent
          agreements between Licensee and TelmarHelixa.
        </Paragraph>
      </Section>

      <Section id="telmarhelixa-rights" title="4. TelmarHelixa’s Rights">
        <Paragraph>
          TelmarHelixa represents and Licensee and User acknowledge that the
          Services, Platform and related documentation are proprietary products
          of TelmarHelixa or its licensor(s) protected under international
          copyright, trademark, trade secret and patent laws. Licensee and User
          further acknowledge and agree that, as between the Parties, all right,
          title, and interest in and to the Services, Platform, the Feedback (as
          defined in section 5 below), and related documentation, including
          associated Intellectual Property Rights, are and shall remain with
          TelmarHelixa. Other than the limited rights granted hereunder, these
          Terms do not convey to Licensee or User an interest in or to the
          Services, and/or Platform. TelmarHelixa reserves the right to suspend
          access to the Services and/or Platform or terminate these Terms at any
          time. The provisions of this section shall survive termination of
          these Terms.
        </Paragraph>
      </Section>

      <Section id="feedback" title="5. Feedback">
        <Paragraph>
          Licensee and User agree to provide TelmarHelixa, on a weekly basis,
          all relevant and constructive ideas, comments, suggestions, or other
          feedback on the features or functionality of the Services (“
          <strong>Feedback</strong>”). Licensee shall take all appropriate and
          necessary actions to ensure any Intellectual Property Rights are
          transferred to and vest in TelmarHelixa. Licensee shall not assert any
          ownership rights in the Feedback whatsoever. TelmarHelixa shall,
          without limitation, be entitled to use the Feedback for the purpose of
          improving and creating derivative works of the Services and or use in
          marketing materials for the Services.
        </Paragraph>
        <Paragraph>
          For the purposes of these Terms “
          <strong>Intellectual Property Rights</strong>” means patents,
          trademarks, service marks, registered designs, applications for any of
          those rights, trade and business names, unregistered trademarks and
          service marks, copyrights, know-how, database rights, rights in
          designs and inventions and all other rights of the same or similar
          effect or nature under the laws of any jurisdiction, including all
          renewals, applications and registrations (and the right to apply for
          registration) relating to any of the foregoing.
        </Paragraph>
      </Section>

      <Section id="restrictions" title="6. Restrictions">
        <Paragraph>
          Licensee and User shall not: (i) make the Services and/or Platform or
          its user credentials available to anyone else; (ii) sell, resell,
          transfer, rent, lease, copy, replicate and/or reverse-engineer the
          Services and/or Platform; (iii) use the Services and/or Platform to
          store or transmit infringing, obscene, libelous, or otherwise unlawful
          or tortious material, or to store or transmit material in violation of
          third party privacy rights and/or third party Intellectual Property
          Rights; (iv) use the Services and/or Platform to send spam or
          otherwise duplicative or unsolicited messages in violation of
          applicable law; (v) use the Services and/or Platform to store or
          transmit any automatic restraint, viruses, worms, time bombs, trojan
          horses and other harmful or malicious code, files, scripts, agents or
          programs that interfere with the provision of the Services and/or
          Platform; (vi) interfere with, adversely impact, or disrupt the
          integrity or performance of the Services and/or Platform or any data
          contained therein; (vii) attempt to gain unauthorised access to the
          Services and/or Platform or their related systems or networks; or
          (viii) access the Services and/or Platform for purposes of monitoring
          their availability, performance or functionality, or for any other
          benchmarking or competitive purposes. Upon the occurrence of any of
          the events listed above, TelmarHelixa may suspend Licensee and User
          access to the Services and/or Platform. These Terms do not permit
          Licensee or User to use the Services and/or Platform for any purpose
          other than the Purpose, including but not limited to any commercial
          application or in the operation of Licensee’s business.
        </Paragraph>
      </Section>

      <Section id="exclusion-of-warranties" title="7. Exclusion of Warranties">
        <Paragraph>
          THE SERVICES AND PLATFORM ARE PROVIDED TO LICENSEE &quot;AS IS&quot;,
          AND ANY USE BY LICENSEE OF THE SERVICES AND PLATFORM DURING THE
          EVALUATION PERIOD WILL BE AT LICENSEE’S AND USER’S SOLE RISK.
          TELMARHELIXA MAKES NO WARRANTIES RELATING TO THE SERVICES AND/OR
          PLATFORM AND EXPRESSLY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED,
          INCLUDING WITHOUT LIMITATION THOSE OF MERCHANTABILITY, OR FITNESS FOR
          A PARTICULAR PURPOSE. The foregoing provisions shall be enforceable to
          the maximum extent permitted by applicable law.
        </Paragraph>
      </Section>

      <Section id="limitation-of-liability" title="8. Limitation of Liability">
        <Paragraph>
          Nothing in these Terms shall limit or exclude liability which cannot
          be limited under applicable law. Subject to the foregoing, except for
          TelmarHelixa’s obligations under section 9 below, TelmarHelixa’s
          cumulative liability to Licensee and User or any other party for any
          loss or damages resulting from any claims, demands, or actions arising
          out of or relating to these Terms shall not exceed $100. In no event
          shall TelmarHelixa be liable for any indirect, incidental,
          consequential, special, or exemplary damages or lost profits, even if
          Licensee and User have been advised of the possibility of such
          damages.
        </Paragraph>
      </Section>

      <Section id="indemnification" title="9. Indemnification">
        <Paragraph>
          TelmarHelixa shall defend, indemnify and hold Licensee harmless from
          and against all losses and costs based on, arising out of or otherwise
          in connection with any claim (threatened or actual) made by a third
          party based upon infringement or misappropriation of any Intellectual
          Property Rights in connection with the Services and/or Platform
          provided that: (i) TelmarHelixa is given immediate and complete
          control of such claim; (ii) Licensee does not prejudice TelmarHelixa’s
          defence of such claim; (iii) Licensee, at TelmarHelixa’s expense gives
          TelmarHelixa all reasonable assistance with such claim; (iv) such
          claim does not arise from an intentional tortious act by Licensee or
          the negligence of Licensee; (v) such claim is not based upon the use
          of the Services and/or Platform by Licensee in an application or
          environment for which the Services and/or Platform were not designed
          or contemplated; (vi) such claim does not arise as a result of
          modifications and/or improvements of the Services and/or Platform
          introduced or made by Licensee; and (vii) such claim does not arise as
          a result of the use of the Services and/or Platform in combination
          with any equipment or computer programs not supplied or approved by
          TelmarHelixa. Licensee shall indemnify and hold TelmarHelixa harmless
          from and against any and all claims, costs, damages, losses,
          liabilities and expenses (including attorneys&apos; fees and costs) to
          the extent arising out or in connection with a claim alleging that use
          of a Dataset infringes the Intellectual Property Rights of a third
          party and/or Applicable Data Protection Law, provided in any such case
          that TelmarHelixa: (i) promptly gives notice of the claim to Licensee;
          (ii) gives Licensee sole control of the defence and settlement of the
          claim (provided that Licensee may not settle such claim unless such
          settlement unconditionally releases TelmarHelixa of all liability and
          does not adversely affect TelmarHelixa’s business or the Services
          and/or Platform); (iii) provides to Licensee all available information
          and reasonable assistance; and (iv) has not compromised or settled
          such third-party claim. For the purposes of these Terms, “
          <strong>Dataset</strong>” means a Licensee proprietary database
          created by Licensee or commissioned and created for its sole use, that
          is uploaded to the Services by a User and is used for data analysis
          purposes in conjunction with TelmarHelixa’s other services that
          Licensee may separately subscribe to.
        </Paragraph>
      </Section>

      <Section id="confidentiality" title="10. Confidentiality">
        <Paragraph>
          Each Party shall treat as confidential for a period of two (2) years
          from the Effective Date all information received or obtained in
          conjunction with, and as a result of entering into or performing these
          Terms and which is designated confidential or which ought reasonably
          to be regarded as confidential. This section shall survive termination
          of these Terms. Information shall not be deemed to be confidential if
          such information: (a) is, or becomes, publicly known through means
          other than a breach of these Terms; (b) is in the possession of the
          Party receiving such information prior to receipt thereof from the
          other Party; (c) is independently developed by the other Party; (d) is
          the subject of any litigation in which the Parties hereto are adverse
          parties; or (e) is lawfully disclosed by a third party who is not
          subject to a confidentiality agreement or obligation relating to such
          disclosure. Notwithstanding the foregoing, all information can be
          provided to a Party’s employees, advisors and Affiliates who need to
          know such information, where “<strong>Affiliate</strong>” means any
          entity which directly or indirectly controls, is controlled by, or is
          under common control of the subject entity and “control,” for purposes
          of this definition, means direct or indirect ownership or control of
          more than fifty percent (50%) of the voting interests of the subject
          entity.
        </Paragraph>
        <Paragraph>
          If requested or required by law, regulation or interrogatory, request
          for information or documents, subpoena, deposition, civil
          investigative demand other legal, governmental or regulatory process,
          or request of any governmental agency or regulatory body having or
          claiming to have authority to regulate the relevant Party’s business
          to disclose any information, it is agreed that such information may be
          disclosed to the extent that the relevant Party concludes in good
          faith that it is required to do so.
        </Paragraph>
      </Section>

      <Section id="data-protection" title="11. Data Protection">
        <SubHeading>Definitions</SubHeading>
        <Paragraph>
          In this section, the following terms shall have the following
          meanings:
        </Paragraph>
        <Bullets>
          <Bullet>
            “<strong>controller</strong>”, “<strong>processor</strong>”, “
            <strong>data subject</strong>”, “<strong>personal data</strong>”, “
            <strong>processing</strong>” (and “<strong>process</strong>”) and “
            <strong> special categories of personal data</strong>” shall have
            the meanings given in Applicable Data Protection Law.
          </Bullet>
          <Bullet>
            “<strong>Applicable Data Protection Law</strong>” shall mean all
            laws applicable (in whole or part) to a Party’s processing of
            personal data under or in connection with these Terms, including, as
            applicable, EU Data Protection Law, UK Data Protection Law and the
            CCPA, as enacted, amended or superseded from time to time.
          </Bullet>
          <Bullet>
            “Business” shall have the meaning given to it in the CCPA.
          </Bullet>
          <Bullet>
            “<strong>CCPA</strong>” means the California Consumer Privacy Act of
            2018 (Cal. Civ. Code §§ 1798.100 to 1798.199), as amended from time
            to time, and any related regulations and guidance provided or issued
            by the California Attorney General pertaining to the same.
          </Bullet>
          <Bullet>
            “EU Data Protection Law” means: (i) all EU regulations or other
            legislation applicable to the processing of personal data under
            these Terms (such as Regulation (EU) 2016/679) (the “
            <strong>GDPR</strong>”); (ii) the national laws of each EEA member
            state implementing any EU directive applicable to the processing of
            personal data under these Terms (such as Directive 2002/58/EC (the
            “e-Privacy Directive”); and (iii) any other national laws of each
            EEA member state applicable to the processing of personal data under
            these Terms, as amended or superseded from time to time.
          </Bullet>
          <Bullet>
            “<strong>Privacy Policy</strong>” means TelmarHelixa’s privacy
            policy currently available at:{' '}
            <DocLink href="https://telmarhelixa.com/privacy-notice/" isExternal>
              https://telmarhelixa.com/privacy-notice/
            </DocLink>
            .
          </Bullet>
          <Bullet>
            “Service Provider” shall have the meaning given to it in the CCPA.
          </Bullet>
          <Bullet>
            “<strong>Third Party</strong>” means a person or entity other than
            TelmarHelixa and Licensee.
          </Bullet>
          <Bullet>
            “<strong>UK Data Protection Law</strong>” means: (i) the GDPR as it
            forms part of UK law by virtue of section 3 of the European Union
            (Withdrawal) Act 2018 (the “<strong>UK GDPR</strong>”); (ii) the
            Data Protection Act 2018 (the “<strong>DPA 2018</strong>”); (iii)
            the Privacy and Electronic Communications (EC 2018”); (iii) the
            Privacy and Electronic Communications (EC Directive) Regulations
            2003 as they continue to have effect by virtue of section 2 of the
            European Union (Withdrawal) Act 2018 (“<strong>PECR</strong>”); and
            (iv) any other laws in force in the UK from time to time applicable
            to the processing of personal data under these Terms, as amended or
            superseded from time to time.
          </Bullet>
        </Bullets>

        <SubHeading>Relationship of the Parties:</SubHeading>
        <Paragraph>
          The Parties acknowledge and agree that pursuant to these Terms,
          TelmarHelixa may receive or otherwise collect Datasets (as defined in
          section 9 of these Terms) for processing. By using the Services,
          Licensee will share Dataset(s) with TelmarHelixa for the purposes of
          enabling TelmarHelixa to provide the Services, and other associated
          services that Licensee separately subscribes to.
        </Paragraph>
        <Paragraph>
          Licensee acknowledges that by engaging and requiring TelmarHelixa to
          provide the Services, the information that is made available to
          TelmarHelixa by Licensee (either directly or indirectly) under these
          Terms (or that information in conjunction with other information
          regarding individuals) may constitute personal data (or personal
          information) under Applicable Data Protection Law.
        </Paragraph>
        <Paragraph>
          Where TelmarHelixa is a controller of such personal data (for example,
          in relation to User contact details and passwords in order to provide
          Licensee with the Services or for general business management
          purposes) (“<strong>Ancillary Data</strong>”), TelmarHelixa shall use
          the Ancillary Data in the manner set out in its Privacy Policy.
        </Paragraph>
        <Paragraph>
          Where TelmarHelixa is the processor of such personal data, section
          11.3 of these Terms shall apply.
        </Paragraph>
        <Paragraph>
          Where TelmarHelixa is a processor under these Terms, Licensee
          warrants, represents and agrees that:
        </Paragraph>
        <Bullets>
          <Bullet>
            it has all rights, permissions and consents required by law or
            contract to enable TelmarHelixa to access and use the Data (as
            defined below) in order for TelmarHelixa to provide the Services to
            Licensee under these Terms; and
          </Bullet>
          <Bullet>
            it has satisfied the requirements of Applicable Data Protection Law
            in relation to any Datasets that it makes available to TelmarHelixa
            (or that it otherwise requires TelmarHelixa to access or use) under
            these Terms.
          </Bullet>
        </Bullets>
        <Paragraph>
          Each Party shall comply with the obligations that apply to it under
          Applicable Data Protection Law.
        </Paragraph>

        <SubHeading>TelmarHelixa as processor</SubHeading>
        <Paragraph>
          Licensee (the controller) appoints TelmarHelixa as a processor to
          process the personal data contained in a Dataset (if any) (the “
          <strong>Data</strong>”) for the purposes of providing the Services
          (and as further described in Annex 1 to these Terms or as otherwise
          agreed in writing by the Parties), (the “
          <strong>Permitted Purpose</strong>”). If TelmarHelixa becomes aware
          that processing for the Permitted Purpose infringes Applicable Data
          Protection Law, it shall promptly inform Licensee.
        </Paragraph>
        <Paragraph>
          TelmarHelixa shall act solely as a Service Provider with respect to
          any Data received. The Parties acknowledge and agree that Licensee is
          providing the Data to TelmarHelixa to enable TelmarHelixa’s
          performance of the Services in accordance with the Permitted Purpose,
          and that Data is not exchanged for monetary or other valuable
          consideration.
        </Paragraph>
        <Paragraph>
          Without limiting any of the foregoing, Data shall be used only in the
          manner contemplated in these Terms and shall not intentionally be
          disclosed or sold (as such term is defined under the CCPA) to Third
          Parties without Licensee’s prior written consent, or as otherwise
          permitted by the CCPA or Applicable Data Protection Law. TelmarHelixa
          hereby certifies that it understands and will comply with the
          restrictions and obligations contained in these Terms. Notwithstanding
          anything to the contrary, TelmarHelixa may aggregate, de-identify, or
          anonymize Data, so it no longer meets the definition of personal
          information under the CCPA, and may use such aggregated,
          de-identified, or anonymized data for its own purposes. TelmarHelixa
          will not attempt to or re-identify any previously aggregated,
          de-identified, or anonymized data. For the avoidance of doubt, to the
          extent permitted by the CCPA, TelmarHelixa may use Data to detect data
          security incidents or protect against fraudulent or illegal activity,
          or improve its services. To the extent required by Applicable Data
          Protection Law, this section constitutes TelmarHelixa’s certification
          to the processing restrictions herein.
        </Paragraph>
        <Paragraph>
          <strong>International transfers:</strong> To the extent applicable,
          TelmarHelixa shall not transfer the Data outside of the European
          Economic Area (“<strong>EEA</strong>”) unless it has taken such
          measures as are necessary to ensure the transfer is in compliance with
          Applicable Data Protection Law. Licensee acknowledges and agrees that
          TelmarHelixa may transfer the Data to its Affiliates under the
          TelmarHelixa intragroup data transfer agreement, which incorporates
          the standard contractual clauses as approved by the European
          Commission.
        </Paragraph>
        <Paragraph>
          <strong>Confidentiality of processing:</strong> TelmarHelixa shall
          ensure that any person it authorises to process the Data shall protect
          the Data in accordance with TelmarHelixa’s confidentiality obligations
          under these Terms.
        </Paragraph>
        <Paragraph>
          <strong>Security:</strong> The processor shall implement technical and
          organisational measures to protect the Data (i) from accidental or
          unlawful destruction, and (ii) loss, alteration, unauthorised
          disclosure of, or access to the Data (a “
          <strong>Security Incident</strong>”).
        </Paragraph>
        <Paragraph>
          <strong>Subcontracting:</strong> Licensee consents to TelmarHelixa
          engaging Third Party subprocessors to process the Data for the
          Permitted Purpose provided that: (i) TelmarHelixa maintains an
          up-to-date list of its subprocessors at{' '}
          <DocLink
            href="https://telmarhelixa.com/telmarsubprocessors/"
            isExternal
          >
            https://telmarhelixa.com/telmarsubprocessors/
          </DocLink>
          , which it shall update with details of any change in subprocessors at
          least ten (10) days’ prior to any such change; (ii) TelmarHelixa
          imposes data protection terms on any subprocessor it appoints that
          require it to protect the Data to the standard required by Applicable
          Data Protection Law; and (iii) TelmarHelixa remains liable for any
          breach of this section 11.3 that is caused by an act, error or
          omission of its subprocessor. Licensee may object to TelmarHelixa’s
          appointment or replacement of a subprocessor prior to its appointment
          or replacement, provided such objection is based on reasonable grounds
          relating to data protection. In such an event, TelmarHelixa will
          either not appoint or replace the subprocessor or, if this is not
          possible, Licensee may suspend or terminate these Terms (without
          prejudice to any fees incurred by Licensee prior to suspension or
          termination).
        </Paragraph>
        <Paragraph>
          <strong>Cooperation and data subjects’ rights:</strong> TelmarHelixa
          shall provide reasonable and timely assistance to Licensee (at
          Licensee’s expense) to enable Licensee to (i) meet its obligations
          under Applicable Data Protection Laws and in particular to respond to
          any request from a data subject to exercise any of its rights under
          Applicable Data Protection Law (including its rights of access,
          correction, objection, erasure and data portability, as applicable);
          and (ii) respond to any other correspondence, enquiry or complaint
          received from a data subject, regulator or other Third Party in
          connection with the processing of the Data. In the event that any such
          request, correspondence, enquiry or complaint is made directly to
          TelmarHelixa concerning TelmarHelixa’s processor activities under
          these Terms, TelmarHelixa shall promptly inform Licensee providing
          full details of the same. TelmarHelixa hereby certifies that it
          understands and will comply with the restrictions and obligations
          contained in these Terms. To the extent required by Applicable Data
          Protection Law, this section constitutes TelmarHelixa’s certification
          to the processing restrictions herein.
        </Paragraph>
        <Paragraph>
          <strong>Data Protection Impact Assessment:</strong> TelmarHelixa shall
          provide reasonable cooperation to Licensee (at Licensee’s expense) in
          connection with any data protection impact assessment that may be
          required under Applicable Data Protection Law.
        </Paragraph>
        <Paragraph>
          <strong>Security Incidents:</strong> If it becomes aware of a
          confirmed Security Incident, TelmarHelixa shall inform Licensee
          without undue delay and shall provide reasonable information and
          cooperation to Licensee so that Licensee can fulfil any data breach
          reporting obligations it may have under (and in accordance with the
          timescales required by) Applicable Data Protection Law. TelmarHelixa
          shall further take such any reasonably necessary measures and actions
          to remedy or mitigate the effects of the Security Incident and shall
          keep Licensee informed of all material developments in connection with
          the Security Incident.
        </Paragraph>
        <Paragraph>
          <strong>Deletion or return of Data:</strong> Upon termination or
          expiry of these Terms, TelmarHelixa shall (at Licensee’s election)
          destroy or return to Licensee all Data in its possession or control.
          This requirement shall not apply to the extent that TelmarHelixa is
          required by applicable law to retain some or all of the Data, or to
          Data it has archived on back-up systems, in which event TelmarHelixa
          shall securely isolate and protect from any further processing except
          to the extent required by such law until deletion is possible. For the
          avoidance of doubt, this requirement also does not apply where
          TelmarHelixa has been instructed to provide Services via API (in
          contrast to where TelmarHelixa hosts and stores the relevant Licensee
          Dataset on its own servers), as in such case TelmarHelixa will have no
          access to the underlying Data once that Service has been terminated or
          expired.
        </Paragraph>
        <Paragraph>
          <strong>Audit:</strong> To the extent required by Applicable Data
          Protection Law, TelmarHelixa shall permit Licensee (or its appointed
          Third Party auditors) to audit TelmarHelixa’s compliance with this
          section 11.3, and shall make available to Licensee all information,
          systems and staff reasonably necessary for Licensee (or its Third
          Party auditors) to conduct such audit. In such case, TelmarHelixa
          acknowledges that Licensee (or its Third Party auditors) may enter its
          premises for the purposes of conducting this audit, provided that
          Licensee gives it reasonable prior notice of its intention to audit,
          conducts its audit during normal business hours, and takes all
          reasonable measures to prevent unnecessary disruption to
          TelmarHelixa’s operations. Licensee will not exercise its audit rights
          more than once in any twelve (12) calendar month period, except: (i)
          if and when required by instruction of a competent data protection
          authority; or (ii) Licensee reasonably believes a further audit is
          necessary due to a Security Incident suffered by TelmarHelixa.
        </Paragraph>
      </Section>

      <Section id="general" title="12. General">
        <Bullets>
          <Bullet>
            No relaxation, forbearance, delay or indulgence by either Party in
            enforcing any of the terms and conditions of these Terms or the
            granting of time by either Party to the other shall prejudice,
            affect or restrict the rights and powers of that said party
            hereunder nor shall any waiver by either Party of any breach hereof
            operate as a waiver of any subsequent or any continuing breach
            hereof.
          </Bullet>
          <Bullet>
            No amendment or other variation of these Terms shall be effective
            unless it is in writing, is dated, and is signed by or on behalf of
            each of the Parties.
          </Bullet>
          <Bullet>
            Neither Party may assign these Terms in whole or any part to any
            third party.
          </Bullet>
          <Bullet>
            The: (i) TelmarHelixa entity entering into these Terms; (ii) address
            to which Licensee should direct notices to TelmarHelixa under these
            Terms; (iii) the governing law; and (iv) courts that will have
            exclusive jurisdiction over any such dispute or lawsuit shall be
            determined by where Licensee is domiciled. Such factors shall be
            determined in accordance with the table in Annex 1 of these Terms.
            Each Party agrees to the applicable governing law set out in the
            table in Annex 1 of these Terms without regard to choice of
            conflicts of law rules and to the exclusive jurisdiction of the
            listed courts in such section.
          </Bullet>
          <Bullet>
            If any provision of these Terms or any part of any such provision is
            held to be invalid or unenforceable, such provision or part (as the
            case may be) shall be ineffective only to the extent of such
            invalidity or unenforceability, without rendering invalid or
            unenforceable or otherwise prejudicing or affecting the remainder of
            such provision or any other provision hereof.
          </Bullet>
        </Bullets>
      </Section>

      <Section id="annex-1" title="ANNEX 1">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr>
                {ANNEX_HEADERS.map((header) => (
                  <th key={header} className={CELL_CLASS} scope="col">
                    <Text tag="span" size="xbody" weight="bold">
                      {header}
                    </Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ANNEX_ROWS.map((row) => (
                <tr key={String(row[0])}>
                  {row.map((cell, index) => (
                    <td
                      key={`${String(row[0])}-${index}`}
                      className={CELL_CLASS}
                    >
                      {typeof cell === 'string' ? (
                        <Text tag="span" size="xbody">
                          {cell}
                        </Text>
                      ) : (
                        cell
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </article>
  </div>
);
